import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyRound, Package, TrendingUp, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionLabel, StatusBadge } from "@/components/bits";
import KeyVaultCard from "@/components/KeyVaultCard";
import DiscordConnectCard from "@/components/DiscordConnectCard";

export default function Dashboard() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [codes, setCodes] = useState([]);
  const [rank, setRank] = useState(null);
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/creator/mine").then((r) => setCodes(r.data)).catch(() => {});
    api.get("/ranks/mine").then((r) => setRank(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.discord_id) api.get("/auth/discord/activity").then((r) => setActivity(r.data)).catch(() => {});
  }, [user?.discord_id]);

  const totalEarned = codes.reduce((s, c) => s + (c.commission_earned || 0), 0);
  const vaultItems = useMemo(() => orders.flatMap((order) => {
    const items = order.items?.length ? order.items : [{
      slug: order.slug, product_name: order.product_name, category: order.category,
      tier: order.tier, quantity: order.quantity || 1,
      keys: order.keys?.length ? order.keys : [order.license_key].filter(Boolean),
      key_status: order.key_status, expires_at: order.expires_at,
      loader_url: order.loader_url, tutorial_url: order.tutorial_url,
      setup_kind: order.category === "account" ? "nfa" : order.category === "cheat" ? "cheat" : null,
      can_renew: false,
    }];
    return items.map((item, itemIndex) => ({ order, item, itemIndex }));
  }), [orders]);

  return (
    <div className="pt-32 pb-28 mx-auto max-w-[1200px] px-5 sm:px-8" data-testid="dashboard-page">
      <SectionLabel>OPERATIVE DASHBOARD</SectionLabel>
      {user?.discord_id ? (
        <div className="mt-4 relative overflow-hidden rounded-2xl border border-white/10" data-testid="vault-identity">
          {user.discord_banner_url && (
            <img src={user.discord_banner_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 grayscale-img" />
          )}
          <div className="relative flex flex-wrap items-center gap-5 bg-gradient-to-r from-black via-black/75 to-black/30 p-6 sm:p-8">
            {user.discord_avatar_url && (
              <img src={user.discord_avatar_url} alt="" className="h-16 w-16 rounded-full border border-white/20 object-cover" data-testid="vault-avatar" />
            )}
            <div className="min-w-0">
              <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none">
                Welcome, {user.discord_global_name || user.discord_username || user.name}
              </h1>
              <div className="mt-2 font-mono text-[10px] tracking-[0.2em] text-white/40">{user.email}</div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {user.discord_og && (
                  <span className="rounded-full border border-white/30 bg-white/10 px-2.5 py-1 font-mono text-[9px] tracking-[0.2em] text-white" data-testid="og-badge">SERVER OG</span>
                )}
                {user.discord_guild_joined_at && (
                  <span className="rounded-full border border-white/12 px-2.5 py-1 font-mono text-[9px] tracking-[0.2em] text-white/50" data-testid="member-since">
                    MEMBER SINCE {new Date(user.discord_guild_joined_at).toLocaleDateString(undefined, { month: "short", year: "numeric" }).toUpperCase()}
                  </span>
                )}
                {activity?.online && (
                  <span className="rounded-full border border-emerald-300/30 px-2.5 py-1 font-mono text-[9px] tracking-[0.2em] text-emerald-200" data-testid="vault-activity">
                    {activity.game ? `IN GAME: ${String(activity.game).toUpperCase()}` : "ONLINE NOW"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-display text-5xl sm:text-6xl uppercase leading-none">Welcome, {user?.name}</h1>
          <div className="font-mono text-xs tracking-[0.2em] text-white/40">{user?.email}</div>
        </div>
      )}

      <div className="mt-8">
        <DiscordConnectCard required={searchParams.get("discord") === "required"} />
      </div>

      {/* stats */}
      <div className="mt-12 grid gap-px rounded-2xl overflow-hidden bg-white/10 border border-white/10 sm:grid-cols-3" data-testid="dashboard-stats">
        <div className="bg-black p-8">
          <Package size={20} className="text-white/60" />
          <div className="mt-4 font-display text-4xl">{orders.length}</div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-white/40 mt-1">ACTIVE PRODUCTS</div>
        </div>
        <div className="bg-black p-8">
          <Users size={20} className="text-white/60" />
          <div className="mt-4 font-display text-4xl">{codes.reduce((s, c) => s + (c.uses || 0), 0)}</div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-white/40 mt-1">CODE REDEMPTIONS</div>
        </div>
        <div className="bg-black p-8">
          <TrendingUp size={20} className="text-white/60" />
          <div className="mt-4 font-display text-4xl">${totalEarned.toFixed(2)}</div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-white/40 mt-1">CREATOR EARNINGS</div>
        </div>
      </div>

      {/* buyer rank */}
      {rank && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-black p-8" data-testid="rank-card">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] text-white/40">YOUR RANK</div>
              <div className="mt-2 font-display text-5xl uppercase leading-none" data-testid="rank-name">{rank.name}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl">${rank.spend.toFixed(2)}</div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-white/40">LIFETIME SPEND</div>
            </div>
          </div>
          <div className="mt-6 h-1 rounded-full bg-white/10 relative overflow-hidden" data-testid="rank-progress">
            <div
              className="absolute inset-y-0 left-0 bg-white transition-all duration-700"
              style={{ width: `${rank.next ? Math.min(100, (rank.spend / rank.next.threshold) * 100) : 100}%` }}
            />
          </div>
          <div className="mt-3 font-mono text-[10px] tracking-[0.15em] text-white/45">
            {rank.next
              ? `// $${(rank.next.threshold - rank.spend).toFixed(2)} TO ${rank.next.name} — UNLOCKS ${rank.next.pct}% OFF EVERY ORDER FOREVER`
              : `// MAX RANK — ${rank.pct}% OFF EVERY ORDER FOREVER`}
          </div>
          {rank.pct > 0 && (
            <div className="mt-2 font-mono text-[10px] tracking-[0.15em] text-white/70" data-testid="rank-perk">
              {`// ACTIVE PERK: ${rank.pct}% OFF AUTO-APPLIES AT CHECKOUT (CREATOR CODES STILL WIN)`}
            </div>
          )}
        </div>
      )}

      {/* creator codes */}
      {codes.length > 0 && (
        <div className="mt-16" data-testid="creator-earnings">
          <h2 className="font-display text-3xl uppercase mb-6">Creator Codes</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {codes.map((c) => (
              <div key={c.code} className="rounded-2xl border border-white/15 p-6">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-lg tracking-[0.2em]">{c.code}</span>
                  <StatusBadge status={c.active ? "undetected" : "down"} small />
                </div>
                <div className="mt-5 grid grid-cols-3 gap-4">
                  <div><div className="font-display text-2xl">{c.uses}</div><div className="font-mono text-[9px] tracking-[0.15em] text-white/40">USES</div></div>
                  <div><div className="font-display text-2xl">${c.total_sales.toFixed(0)}</div><div className="font-mono text-[9px] tracking-[0.15em] text-white/40">SALES</div></div>
                  <div><div className="font-display text-2xl">${c.commission_earned.toFixed(2)}</div><div className="font-mono text-[9px] tracking-[0.15em] text-white/40">EARNED</div></div>
                </div>
                <div className="mt-4 font-mono text-[10px] tracking-[0.15em] text-white/30">{c.discount_pct}% BUYER DISCOUNT · {c.commission_pct}% COMMISSION</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* key vault */}
      <div className="mt-16" data-testid="orders-list">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <SectionLabel>LIVE LICENSE OPERATIONS</SectionLabel>
            <h2 className="mt-3 font-display text-3xl uppercase">Key Vault</h2>
          </div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-white/35">
            {vaultItems.length} ITEM{vaultItems.length === 1 ? "" : "S"} TRACKED
          </div>
        </div>
        {orders.length === 0 ? (
          <div className="rounded-2xl border border-white/10 p-12 text-center">
            <KeyRound size={28} className="mx-auto text-white/30" />
            <p className="mt-4 text-white/50">No purchases yet.</p>
            <Link to="/store" className="btn-inj mt-6 inline-flex px-8 py-3.5 text-sm">BROWSE STORE</Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="key-vault">
            {vaultItems.map(({ order, item, itemIndex }) => (
              <KeyVaultCard key={`${order.session_id}-${itemIndex}`} order={order} item={item} index={itemIndex} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
